import React, { FC, useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  OutlinedInput,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  getBoardRegistrations,
  fetchBoardRegistrations,
  getBoardRegistrationsLoading,
} from "../../../features/tournoi/tournoiSlice";
import { Participation, Tableau } from "../../../types";
import { arrayToCsv, createCsvBlob } from "../../../utils/arrayHelper";

interface BoardRegistrationsProps {}

const BoardRegistrations: FC<BoardRegistrationsProps> = () => {
  const dispatch = useAppDispatch();
  const boardRegistrations = useAppSelector(getBoardRegistrations);
  const loading = useAppSelector(getBoardRegistrationsLoading);

  const [currentBoardId, setCurrentBoardId] = useState<string>("");
  const [currentBoardRegistrations, setCurrentBoardRegistrations] = useState<
    Participation[]
  >([]);
  const [sortField, setSortField] = useState<string | null>("date");
  const [sortAsc, setSortAsc] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState<string>("all");
  const [checkinFilter, setCheckinFilter] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    dispatch(fetchBoardRegistrations());
  }, [dispatch]);

  useEffect(() => {
    if (boardRegistrations.length > 0) {
      const defaultBoard = boardRegistrations[0];
      setCurrentBoardId(defaultBoard.id);
      setCurrentBoardRegistrations(defaultBoard.participation || []);
    }
  }, [boardRegistrations]);

  const handleBoardChange = (event: SelectChangeEvent) => {
    const selectedBoard = boardRegistrations.find(
      (board) => board.id === event.target.value
    );
    if (selectedBoard) {
      setCurrentBoardId(selectedBoard.id);
      setCurrentBoardRegistrations(selectedBoard.participation || []);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  // Fonction pour gérer les 4 valeurs de pointage (définie avant son utilisation)
  const getCheckinStatus = (participation: Participation): string => {
    if (participation.isPostClosureRegistration) {
      return participation.checkedIn ? "Nouveau pointé" : "Nouveau Non pointé";
    } else {
      return participation.checkedIn ? "Pointé" : "Non pointé";
    }
  };

  const checkinStatusOptions = [
    "Pointé",
    "Non pointé",
    "Nouveau pointé",
    "Nouveau Non pointé",
  ];

  const handleCheckinFilterChange = (event: any) => {
    const value = event.target.value;
    setCheckinFilter(typeof value === "string" ? value.split(",") : value);
  };

  const filteredAndSortedRegistrations = useMemo(() => {
    const filtered = currentBoardRegistrations.filter((p) => {
      // Filtre par recherche
      const searchTarget =
        `${p.players.name} ${p.players.surname} ${p.players.club}`.toLowerCase();
      const searchMatch = searchTarget.includes(searchTerm.toLowerCase());

      // Filtre par paiement
      const paymentMatch =
        paymentFilter === "all" ||
        (paymentFilter === "paid" && p.paymentStatus === "admin") ||
        (paymentFilter === "unpaid" &&
          (p.paymentStatus === "registration" ||
            p.paymentStatus === undefined));

      // Filtre par pointage
      const checkinMatch =
        checkinFilter.length === 0 ||
        checkinFilter.includes(getCheckinStatus(p));

      return searchMatch && paymentMatch && checkinMatch;
    });

    if (!sortField) return filtered;

    return [...filtered].sort((a, b) => {
      let valA: string | number = "";
      let valB: string | number = "";
      switch (sortField) {
        case "name":
          valA = `${a.players.name} ${a.players.surname}`;
          valB = `${b.players.name} ${b.players.surname}`;
          break;
        case "dossard":
          valA = a.players.dossard || 0;
          valB = b.players.dossard || 0;
          break;
        case "points":
          valA = a.players.points;
          valB = b.players.points;
          break;
        case "club":
          valA = a.players.club || "";
          valB = b.players.club || "";
          break;
        case "date":
          valA = new Date(a.dateInscription).getTime();
          valB = new Date(b.dateInscription).getTime();
          break;
        case "pointage":
          valA = getCheckinStatus(a);
          valB = getCheckinStatus(b);
          break;
        case "datePointage":
          valA =
            a.checkedInAt &&
            a.checkedInAt instanceof Date &&
            !isNaN(a.checkedInAt.getTime())
              ? a.checkedInAt.getTime()
              : a.checkedInAt &&
                typeof a.checkedInAt === "object" &&
                "toDate" in a.checkedInAt &&
                typeof a.checkedInAt.toDate === "function"
              ? a.checkedInAt.toDate().getTime()
              : 0;
          valB =
            b.checkedInAt &&
            b.checkedInAt instanceof Date &&
            !isNaN(b.checkedInAt.getTime())
              ? b.checkedInAt.getTime()
              : b.checkedInAt &&
                typeof b.checkedInAt === "object" &&
                "toDate" in b.checkedInAt &&
                typeof b.checkedInAt.toDate === "function"
              ? b.checkedInAt.toDate().getTime()
              : 0;
          break;
        default:
          break;
      }

      // Tri numérique pour les points et dates, alphabétique pour les autres
      if (
        sortField === "points" ||
        sortField === "date" ||
        sortField === "datePointage" ||
        sortField === "dossard"
      ) {
        return (Number(valA) - Number(valB)) * (sortAsc ? 1 : -1);
      } else {
        return (valA > valB ? 1 : -1) * (sortAsc ? 1 : -1);
      }
    });
  }, [
    currentBoardRegistrations,
    searchTerm,
    sortField,
    sortAsc,
    paymentFilter,
    checkinFilter,
  ]);

  // Vérifier si au moins un joueur a un dossard
  const hasAnyDossard = useMemo(() => {
    return currentBoardRegistrations.some((p) => p.players.dossard);
  }, [currentBoardRegistrations]);

  const handleExport = () => {
    const headers = [
      "Nom",
      "Dossard",
      "Points",
      "Licence",
      "Club",
      "Catégorie",
      "Sexe",
      "Email",
      "Date d'inscription",
      "Pointage",
      "Date de pointage",
    ];

    const rows = filteredAndSortedRegistrations.map((p) => [
      `${p.players.name} ${p.players.surname}`,
      p.players.dossard || "",
      p.players.points,
      p.players.licenceId,
      p.players.club,
      p.players.categorie,
      p.players.sexe,
      p.email,
      new Date(p.dateInscription).toLocaleString(),
      getCheckinStatus(p),
      p.checkedInAt &&
      p.checkedInAt instanceof Date &&
      !isNaN(p.checkedInAt.getTime())
        ? p.checkedInAt.toLocaleString()
        : p.checkedInAt &&
          typeof p.checkedInAt === "object" &&
          "toDate" in p.checkedInAt &&
          typeof p.checkedInAt.toDate === "function"
        ? p.checkedInAt.toDate().toLocaleString()
        : "",
    ]);

    const blob = createCsvBlob(headers, rows);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `inscriptions_${Date.now()}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: "80vh" }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: "100%",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Typography variant="h5" fontWeight={600}>
        Liste des inscriptions
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems="center"
      >
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Tableau</InputLabel>
          <Select
            value={currentBoardId}
            label="Tableau"
            onChange={handleBoardChange}
          >
            {boardRegistrations.map((board: Tableau) => (
              <MenuItem key={board.id} value={board.id}>
                {board.name} - {board.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Recherche par Nom ou Club"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 300 }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filtrer par paiement</InputLabel>
          <Select
            label="Filtrer par paiement"
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="paid">Payé</MenuItem>
            <MenuItem value="unpaid">Non payé</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filtrer par pointage</InputLabel>
          <Select
            multiple
            label="Filtrer par pointage"
            value={checkinFilter}
            onChange={handleCheckinFilterChange}
            input={<OutlinedInput label="Filtrer par pointage" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {checkinStatusOptions.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="contained" onClick={handleExport}>
          Exporter CSV
        </Button>
      </Stack>

      <TableContainer component={Paper} sx={{ flexGrow: 1, overflow: "auto" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {[
                { label: "Nom", field: "name" },
                ...(hasAnyDossard
                  ? [{ label: "Dossard", field: "dossard" }]
                  : []),
                { label: "Points", field: "points" },
                { label: "Licence" },
                { label: "Club", field: "club" },
                { label: "Catégorie" },
                { label: "Sexe" },
                { label: "Email" },
                { label: "Date d'inscription", field: "date" },
                { label: "Pointage", field: "pointage" },
                { label: "Date de pointage", field: "datePointage" },
              ].map((col) => (
                <TableCell
                  key={col.label}
                  onClick={col.field ? () => handleSort(col.field) : undefined}
                  sx={{
                    cursor: col.field ? "pointer" : "default",
                    fontWeight: col.field ? 600 : undefined,
                  }}
                >
                  {col.label}
                  {sortField === col.field && (sortAsc ? " ▲" : " ▼")}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedRegistrations.length > 0 ? (
              filteredAndSortedRegistrations.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>{`${p.players.name} ${p.players.surname}`}</TableCell>
                  {hasAnyDossard && (
                    <TableCell>{p.players.dossard || ""}</TableCell>
                  )}
                  <TableCell>{p.players.points}</TableCell>
                  <TableCell>{p.players.licenceId}</TableCell>
                  <TableCell>{p.players.club}</TableCell>
                  <TableCell>{p.players.categorie}</TableCell>
                  <TableCell>{p.players.sexe}</TableCell>
                  <TableCell>{p.email}</TableCell>
                  <TableCell>
                    {new Date(p.dateInscription).toLocaleString()}
                  </TableCell>
                  <TableCell>{getCheckinStatus(p)}</TableCell>
                  <TableCell>
                    {p.checkedInAt &&
                    p.checkedInAt instanceof Date &&
                    !isNaN(p.checkedInAt.getTime())
                      ? p.checkedInAt.toLocaleString()
                      : p.checkedInAt &&
                        typeof p.checkedInAt === "object" &&
                        "toDate" in p.checkedInAt &&
                        typeof p.checkedInAt.toDate === "function"
                      ? p.checkedInAt.toDate().toLocaleString()
                      : ""}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={10}
                  align="center"
                  sx={{ py: 4, fontStyle: "italic", color: "text.secondary" }}
                >
                  Aucune inscription trouvée.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BoardRegistrations;
